import { z } from "zod";

export const MarketSchema = z.enum(["cn", "hk", "fund", "us", "crypto"]);
export const AssetTypeSchema = z.enum([
  "stock",
  "etf",
  "lof",
  "qdii",
  "fund",
  "index",
  "option",
  "future",
  "crypto",
]);
export const CurrencySchema = z.enum(["CNY", "HKD", "USD"]);
export const QuoteProviderIdSchema = z.enum([
  "eastmoney",
  "yahoo",
  "coingecko",
  "akshare",
]);

export const QuoteSecuritySchema = z.object({
  assetId: z.string().trim().min(1).max(128),
  symbol: z.string().trim().min(1).max(32),
  market: MarketSchema,
  type: AssetTypeSchema.default("stock"),
  currency: CurrencySchema.default("CNY"),
  name: z.string().trim().max(128).default(""),
});

export const QuoteMarketSnapshotRequestSchema = z.object({
  securities: z.array(QuoteSecuritySchema).min(1).max(100),
  providerOrder: z.array(QuoteProviderIdSchema).min(1).max(4).optional(),
});

export const QuoteProviderAttemptSchema = z.object({
  providerId: QuoteProviderIdSchema,
  ok: z.boolean(),
  message: z.string(),
  durationMs: z.number().nonnegative(),
});

export const QuoteSnapshotSchema = z.object({
  assetId: z.string(),
  symbol: z.string(),
  name: z.string(),
  price: z.number().positive(),
  previousClose: z.number().nonnegative(),
  open: z.number().nonnegative(),
  high: z.number().nonnegative(),
  low: z.number().nonnegative(),
  volume: z.number().nonnegative(),
  turnover: z.number().nonnegative(),
  pe: z.number().nullable(),
  pb: z.number().nullable(),
  sourceKey: z.string(),
  providerId: QuoteProviderIdSchema,
  updatedAt: z.string(),
  fetchedAt: z.string(),
  delayed: z.boolean(),
  estimated: z.boolean(),
  abnormal: z.boolean(),
  health: z.enum(["ok", "fallback"]),
  attempts: z.array(QuoteProviderAttemptSchema),
});

export const QuoteMarketSnapshotResponseSchema = z.object({
  snapshots: z.array(QuoteSnapshotSchema),
  failedCount: z.number().int().nonnegative(),
});

export const QuoteProviderDescriptorSchema = z.object({
  id: QuoteProviderIdSchema,
  displayName: z.string(),
  runtime: z.literal("server"),
  markets: z.array(MarketSchema),
  assetTypes: z.array(AssetTypeSchema),
  capabilities: z.array(z.enum(["quote", "historical"])),
  requiresAuth: z.literal(true),
  requiresSubscription: z.boolean(),
  requiresServerCredential: z.boolean(),
  rateLimitPerSecond: z.number().positive(),
  cacheTtlSeconds: z.number().int().positive(),
});

export const QuoteProviderDescriptorListSchema = z.array(
  QuoteProviderDescriptorSchema,
);

export const FutuSecuritySchema = z.object({
  assetId: z.string().min(1).max(128),
  symbol: z.string().min(1).max(32),
  market: z.string().min(1).max(16),
  type: z.string().max(32).default(""),
});

export const FutuSnapshotSchema = z.object({
  assetId: z.string(),
  symbol: z.string(),
  name: z.string(),
  price: z.number(),
  previousClose: z.number(),
  open: z.number(),
  high: z.number(),
  low: z.number(),
  volume: z.number(),
  turnover: z.number(),
  pe: z.number().nullable(),
  pb: z.number().nullable(),
  sourceKey: z.string(),
  providerId: z.literal("futu"),
  updatedAt: z.string(),
  fetchedAt: z.string(),
  delayed: z.boolean(),
  estimated: z.boolean(),
  abnormal: z.boolean(),
  health: z.literal("ok"),
  futuCode: z.string(),
});

export const FutuFailureSchema = z.object({
  assetId: z.string(),
  symbol: z.string(),
  futuCode: z.string(),
  message: z.string(),
});

export const FutuMarketSnapshotRequestSchema = z.object({
  securities: z.array(FutuSecuritySchema).min(1).max(100),
});

export const FutuMarketSnapshotResponseSchema = z.object({
  providerId: z.literal("futu"),
  snapshots: z.array(FutuSnapshotSchema),
  failures: z.array(FutuFailureSchema),
});

export const FutuKlinePeriodSchema = z.enum([
  "1m",
  "5m",
  "15m",
  "30m",
  "60m",
  "daily",
  "weekly",
  "monthly",
]);

export const FutuKlineRequestSchema = z.object({
  security: FutuSecuritySchema,
  period: FutuKlinePeriodSchema.default("daily"),
  count: z.number().int().min(1).max(1_000).default(120),
  startDate: z.string().max(16).default(""),
  endDate: z.string().max(16).default(""),
  adjust: z.string().max(8).default("qfq"),
});

export const FutuKlineResponseSchema = z.object({
  providerId: z.literal("futu"),
  assetId: z.string(),
  symbol: z.string(),
  futuCode: z.string(),
  period: FutuKlinePeriodSchema,
  bars: z.array(
    z.object({
      time: z.string(),
      timestamp: z.number().int(),
      open: z.number(),
      high: z.number(),
      low: z.number(),
      close: z.number(),
      volume: z.number(),
      turnover: z.number(),
    }),
  ),
});

export const FundResearchQuerySchema = z.object({
  fundType: z
    .enum(["all", "gp", "hh", "zs", "zq", "qdii", "fof"])
    .default("all"),
  period: z
    .enum(["1d", "mtd", "3m", "6m", "ytd", "1y", "3y", "5y"])
    .default("1y"),
  sortDirection: z.enum(["asc", "desc"]).default("desc"),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce
    .number()
    .pipe(z.union([z.literal(10), z.literal(20), z.literal(30)]))
    .default(20),
});

export const FundHoldingSchema = z.object({
  rank: z.number().int().positive(),
  symbol: z.string(),
  name: z.string(),
  weight: z.number().min(0).max(100),
  sharesWan: z.number().nullable(),
  marketValueWan: z.number().nullable(),
});

export const RankedFundSchema = z.object({
  rank: z.number().int().positive(),
  code: z.string(),
  name: z.string(),
  navDate: z.string(),
  nav: z.number().nullable(),
  returnRate: z.number().nullable(),
  reportDate: z.string().nullable(),
  holdings: z.array(FundHoldingSchema),
  holdingsAvailable: z.boolean(),
});

export const AggregatedFundHoldingSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  occurrenceCount: z.number().int().nonnegative(),
  totalWeight: z.number().nonnegative(),
  averageWeight: z.number().nonnegative(),
  maxWeight: z.number().nonnegative(),
  funds: z.array(z.string()),
  weightsByFund: z.record(z.string(), z.number()),
});

export const FundResearchResponseSchema = z.object({
  fundType: FundResearchQuerySchema.shape.fundType,
  period: FundResearchQuerySchema.shape.period,
  sortDirection: FundResearchQuerySchema.shape.sortDirection,
  page: z.number().int().positive(),
  pageSize: z.union([z.literal(10), z.literal(20), z.literal(30)]),
  totalCount: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
  asOf: z.string(),
  source: z.literal("eastmoney"),
  funds: z.array(RankedFundSchema),
  aggregatedHoldings: z.array(AggregatedFundHoldingSchema),
  failedHoldingCount: z.number().int().nonnegative(),
});

export const StockFundHoldersQuerySchema = z.object({
  symbol: z.string().trim().regex(/^\d{6}$/),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce
    .number()
    .pipe(z.union([z.literal(10), z.literal(20), z.literal(30)]))
    .default(20),
});

export const StockFundHolderSchema = z.object({
  fundCode: z.string(),
  fundName: z.string(),
  managementCompany: z.string().nullable(),
  shares: z.number().nonnegative(),
  marketValueCny: z.number().nonnegative(),
  fundNetAssetRatio: z.number().min(0).max(100).nullable(),
  totalSharesRatio: z.number().min(0).max(100).nullable(),
  freeSharesRatio: z.number().min(0).max(100).nullable(),
});

export const StockFundHoldersResponseSchema = z.object({
  symbol: z.string(),
  stockName: z.string(),
  reportDate: z.string(),
  page: z.number().int().positive(),
  pageSize: z.union([z.literal(10), z.literal(20), z.literal(30)]),
  totalCount: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
  source: z.literal("eastmoney"),
  holders: z.array(StockFundHolderSchema),
});

export type FundResearchQuery = z.infer<typeof FundResearchQuerySchema>;
export type FundResearchResponse = z.infer<typeof FundResearchResponseSchema>;
export type StockFundHoldersQuery = z.infer<
  typeof StockFundHoldersQuerySchema
>;
export type StockFundHoldersResponse = z.infer<
  typeof StockFundHoldersResponseSchema
>;
export type QuoteSecurity = z.infer<typeof QuoteSecuritySchema>;
export type QuoteSnapshot = z.infer<typeof QuoteSnapshotSchema>;
export type QuoteProviderId = z.infer<typeof QuoteProviderIdSchema>;
export type QuoteProviderAttempt = z.infer<typeof QuoteProviderAttemptSchema>;
export type QuoteMarketSnapshotResponse = z.infer<
  typeof QuoteMarketSnapshotResponseSchema
>;
export type QuoteProviderDescriptor = z.infer<
  typeof QuoteProviderDescriptorSchema
>;
